import React, { useEffect, useMemo, useState } from "react";
import { Body1, Body1PointerLink } from "../../../../components/Font";
import { InputElement } from "../../../../ui/Input/Input";
import { useTheme } from "styled-components";
import { LoaderContainer, LoaderEnum, MiniLoader } from "../../../../ui/Loader/MiniLoader";
import styled from "styled-components/macro";
import moment from "moment";
import { startSecondFactor, verifySecondFactor, getSecondFactorOperationState } from "../../api";
// import { useAppDispatch } from "../../../../app/hooks";
import { ConfirmationWayEnum } from "../../../../enum/confirmationWayEnum";
import { Popup } from "../../../../ui/Modal/Popup";

interface Props {
    operationId: number
    token: string
    isAdmin: boolean
    defaultWay?: 'sms' | 'call' | 'callback'
    totalTTLMinutes?: number // Общий TTL процесса 2FA в минутах
    onClose: () => void
    onSuccess: () => void
    onAdminSuccess?: () => void
    onError?: (message: string) => void
}

export const TwoFactorConfirmation: React.FC<Props> = ({ operationId, token, isAdmin, defaultWay = 'callback', totalTTLMinutes, onClose, onSuccess, onAdminSuccess, onError }) => {
    const Theme = useTheme()
    // const dispatch = useAppDispatch()
    const [servicePhone, setServicePhone] = useState<string | null>(null)
    const [code, setCode] = useState("")
    const [sending, setSending] = useState(false)
    const DEFAULT_TTL = 120
    const [countdown, setCountdown] = useState(0)
    const [activeWay, setActiveWay] = useState<'sms' | 'call' | 'callback'>(defaultWay)
    const [activeOperationId, setActiveOperationId] = useState(operationId)
    const [error, setError] = useState<string | null>(null)
    const [verifying, setVerifying] = useState(false)
    const [lastAttemptCode, setLastAttemptCode] = useState<string | null>(null)
    const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null)
    const pollingRef = React.useRef<NodeJS.Timeout | null>(null)
    const callbackCompletedRef = React.useRef(false)
    
    // Общий TTL процесса 2FA
    const [processStartTime] = useState(() => Date.now())
    const [totalTTLSeconds] = useState(() => (totalTTLMinutes ?? 15) * 60)
    const [remainingTTL, setRemainingTTL] = useState(() => (totalTTLMinutes ? (totalTTLMinutes * 60) : null))

    const resolveErrorMessage = useMemo(() => (error: unknown, fallback: string) => {
        if (!error) {
            return fallback
        }
        if (typeof error === 'string') {
            return error || fallback
        }
        if (typeof error === 'object') {
            const err = error as { response?: { data?: { error?: { message?: string, description?: string } } } }
            const message = err?.response?.data?.error?.message || err?.response?.data?.error?.description
            if (message) {
                // Обработка специфичных ошибок 2FA
                if (message.includes('Too many attempts') || message.includes('блокиров')) {
                    return 'Превышено количество попыток. Попробуйте позже.'
                }
                if (message.includes('expired') || message.includes('истек')) {
                    return 'Время подтверждения истекло. Начните вход заново.'
                }
                return message
            }
            return fallback
        }
        return fallback
    }, [])

    const handleResend = async (requestedWay: typeof activeWay) => {
        setSending(true)
        stopPolling();
        try {
            abortControllerRef.current?.abort()
            const controller = new AbortController()
            abortControllerRef.current = controller
            const response = await startSecondFactor(requestedWay, token)
            const newOperationId = response?.operation_id ?? activeOperationId
            setActiveOperationId(newOperationId)
            setActiveWay(requestedWay)
            pollingRef.current = null
            callbackCompletedRef.current = false
            const ttlFromBackend = response?.ttl ?? response?.payload?.ttl ?? response?.retry_after
            setCountdown(typeof ttlFromBackend === 'number' && ttlFromBackend > 0 ? ttlFromBackend : DEFAULT_TTL)
            setError(null)
            setCode("")
            setLastAttemptCode(null)
            const payload = response?.payload
            const nextServicePhone = payload?.service_phone ?? null
            setServicePhone(nextServicePhone)
            if (requestedWay === 'callback' && nextServicePhone) {
                startPolling(newOperationId)
            }
        } catch (e) {
            const message = resolveErrorMessage(e, 'Не удалось отправить код. Попробуйте позже.')
            setError(message)
            onError?.(message)
        } finally {
            setSending(false)
        }
    }

    const getExpectedLength = (way: typeof activeWay) => {
        if (way === 'call') {
            return 4
        }
        if (way === 'callback') {
            return 0
        }
        return 6
    }
    const getInputFormat = (way: typeof activeWay) => {
        if (way === 'call') {
            return '####'
        }
        if (way === 'callback') {
            return undefined
        }
        return '###-###'
    }
    const getPlaceholder = (way: typeof activeWay) => {
        if (way === 'call') {
            return 'Последние 4 цифры номера'
        }
        if (way === 'callback') {
            return 'Ожидаем звонок'
        }
        return 'Код из SMS'
    }

    const getAriaLabel = (way: typeof activeWay) => {
        if (way === 'call') {
            return 'Введите последние 4 цифры номера'
        }
        if (way === 'callback') {
            return 'Ожидание обратного звонка'
        }
        return 'Введите код из SMS'
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (activeWay === 'callback') {
            return
        }
        const text = e.clipboardData.getData('text') || ''
        const normalized = text.replace(/[^0-9]/g, '')
        if (!normalized) {
            return
        }
        e.preventDefault()
        setCode(normalized)
        const expectedLength = getExpectedLength(activeWay)
        if (normalized.length === expectedLength && !sending && !verifying) {
            void handleSubmit(normalized)
        }
    }

    const handleSubmit = async (codeNormalized: string) => {
        if (activeWay === 'callback') {
            await verifyCallback()
            return
        }
        setVerifying(true)
        setLastAttemptCode(codeNormalized)
        try {
            const response = await verifySecondFactor(activeOperationId, codeNormalized, token)
            if (response?.is_succeeded) {
                setError(null)
                if (isAdmin && onAdminSuccess) {
                    onAdminSuccess()
                } else {
                    onSuccess()
                }
                return
            }
            const message = 'Неверный код. Попробуйте ещё раз.'
            setError(message)
            onError?.(message)
        } catch (e) {
            const message = resolveErrorMessage(e, 'Не удалось подтвердить код. Попробуйте ещё раз.')
            setError(message)
            onError?.(message)
        } finally {
            setVerifying(false)
        }
    }

    useEffect(() => {
        if (activeWay === 'callback') {
            return
        }
        const normalized = code.replace(/[^0-9]/g, '')
        const expectedLength = getExpectedLength(activeWay)
        if (normalized.length === expectedLength && !sending && !verifying && normalized !== lastAttemptCode) {
            void handleSubmit(normalized)
        }
    }, [code, sending, verifying, activeOperationId, lastAttemptCode, activeWay])

    useEffect(() => {
        if (activeWay === 'callback') {
            return
        }
        const normalized = code.replace(/[^0-9]/g, '')
        const expectedLength = getExpectedLength(activeWay)
        if (normalized.length < expectedLength && lastAttemptCode !== null) {
            setLastAttemptCode(null)
        }
    }, [code, lastAttemptCode, activeWay])

    useEffect(() => {
        setActiveOperationId(operationId)
        setLastAttemptCode(null)
        setCode("")
        setError(null)
        setCountdown(0)
        setActiveWay(defaultWay)
        stopPolling()
        setServicePhone(null)
        callbackCompletedRef.current = false
    }, [operationId, defaultWay])

    // Подтягиваем service_phone для начального состояния (любой способ), когда первую попытку запустил backend
    useEffect(() => {
        if (!servicePhone && activeOperationId && token) {
            getSecondFactorOperationState(activeOperationId, token)
                .then((resp) => {
                    const nextPhone = resp?.payload?.service_phone ?? null
                    if (nextPhone) {
                        setServicePhone(nextPhone)
                    }
                })
                .catch(() => {
                    // ignore fetch errors silently; phone hint is optional
                })
        }
    }, [servicePhone, activeWay, activeOperationId, token])

    // Если первый способ — callback и номер уже есть, запускаем опрос
    useEffect(() => {
        if (activeWay === 'callback' && servicePhone && activeOperationId && !callbackCompletedRef.current) {
            startPolling(activeOperationId)
        }
    }, [activeWay, servicePhone, activeOperationId])

    const abortControllerRef = React.useRef<AbortController | null>(null)

    // Таймер для countdown resend
    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown(prev => (prev > 0 ? prev - 1 : 0))
        }, 1000)
        return () => clearInterval(timer)
    }, [])

    // Проверка общего TTL процесса 2FA
    useEffect(() => {
        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - processStartTime) / 1000)
            const remaining = totalTTLSeconds - elapsed
            if (totalTTLMinutes) {
                setRemainingTTL(Math.max(0, remaining))
            }
            if (remaining <= 0) {
                setError('Время подтверждения истекло. Начните вход заново.')
                onError?.('Время подтверждения истекло. Начните вход заново.')
                onClose()
            }
        }, 1000)
        return () => clearInterval(timer)
    }, [processStartTime, totalTTLSeconds, onError, onClose, totalTTLMinutes])

    useEffect(() => {
        return () => {
            stopPolling()
            abortControllerRef.current?.abort()
        }
    }, [])

    const startPolling = (operation: number) => {
        if (!operation || callbackCompletedRef.current) {
            return
        }
        stopPolling()
        const interval = setInterval(() => {
            void verifyCallback(operation)
        }, 5000)
        pollingRef.current = interval
        setPollingIntervalId(interval)
    }

    const stopPolling = () => {
        const interval = pollingRef.current ?? pollingIntervalId
        if (interval) {
            clearInterval(interval)
        }
        pollingRef.current = null
        setPollingIntervalId(null)
    }

    useEffect(() => {
        if (activeWay !== 'callback') {
            stopPolling()
        }
        if (activeWay === 'callback') {
            callbackCompletedRef.current = false
        }
    }, [activeWay])

    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                stopPolling()
                return
            }
            if (activeWay === 'callback' && servicePhone && activeOperationId && !callbackCompletedRef.current) {
                startPolling(activeOperationId)
            }
        }
        document.addEventListener('visibilitychange', handleVisibility)
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [activeWay, activeOperationId, servicePhone])

    const verifyCallback = async (opId: number = activeOperationId, currentWay: typeof activeWay = activeWay) => {
        // Если пользователь переключил способ, прекращаем опрос
        if (currentWay !== 'callback') {
            stopPolling()
            return
        }
        try {
            setVerifying(true)
            const response = await verifySecondFactor(opId, null, token, ConfirmationWayEnum.CallBack)
            if (response?.is_succeeded) {
                stopPolling()
                callbackCompletedRef.current = true
                setError(null)
                if (isAdmin && onAdminSuccess) {
                    onAdminSuccess()
                } else {
                    onSuccess()
                }
            } else {
                setError('Ожидаем звонок. Позвоните нам, используя указанный номер.')
            }
        } catch (e) {
            const message = resolveErrorMessage(e, 'Не удалось проверить звонок. Попробуйте позже.')
            setError(message)
            onError?.(message)
        } finally {
            setVerifying(false)
        }
    }

    const handleClose = () => {
        stopPolling()
        abortControllerRef.current?.abort()
        onClose()
    }

    // Маскируем номер из servicePhone (бэкенд теперь возвращает его для всех способов)
    const maskedPhone = useMemo(() => {
        const sourcePhone = servicePhone
        if (!sourcePhone) return null
        const digits = (sourcePhone || '').replace(/\D/g, '')
        if (!digits) return null
        const last4 = digits.slice(-4)
        return `•••••••${last4}`
    }, [servicePhone])

    return (
        <StyledPopup opened={true} title={'Двухфакторная авторизация'} onClose={handleClose}>
            <Container>
                    {activeWay !== 'callback' && (
                        <>
                            {maskedPhone && (
                                <Body1 style={{ marginTop: 4 }} color={Theme.color.secondary}>
                                    {activeWay === 'sms' ? 'Код отправлен на номер ' : 'Звонок поступит на номер '}{maskedPhone}
                                </Body1>
                            )}
                            <InputElement
                                autofocus
                                format={getInputFormat(activeWay)}
                                mask={'_'}
                                value={code}
                                onChange={setCode}
                                placeholder={getPlaceholder(activeWay)}
                                aria-label={getAriaLabel(activeWay)}
                                error={error ?? undefined}
                            />
                            {/* onPaste поддержку добавим через listener на обёртке */}
                            <div style={{position: 'absolute', width: 0, height: 0, overflow: 'hidden'}} onPaste={handlePaste} />
                        </>
                    )}
                    <WaySelector>
                        <Body1PointerLink
                            role={'button'}
                            tabIndex={0}
                            aria-label={'Отправить код по SMS'}
                            onKeyDown={(e) => { if (e.key === 'Enter') { void handleResend('sms') } }}
                            onClick={() => handleResend('sms')}
                            style={{ color: activeWay === 'sms' ? Theme.color.indicator : Theme.color.secondary }}
                        >SMS</Body1PointerLink>
                        <Body1PointerLink
                            role={'button'}
                            tabIndex={0}
                            aria-label={'Обратный звонок'}
                            onKeyDown={(e) => { if (e.key === 'Enter') { void handleResend('call') } }}
                            onClick={() => handleResend('call')}
                            style={{ color: activeWay === 'call' ? Theme.color.indicator : Theme.color.secondary }}
                        >Обратный звонок</Body1PointerLink>
                        <Body1PointerLink
                            role={'button'}
                            tabIndex={0}
                            aria-label={'Получить код через звонок'}
                            onKeyDown={(e) => { if (e.key === 'Enter') { void handleResend('callback') } }}
                            onClick={() => handleResend('callback')}
                            style={{ color: activeWay === 'callback' ? Theme.color.indicator : Theme.color.secondary }}
                        >Звонок</Body1PointerLink>
                    </WaySelector>
                    {activeWay === 'callback' && (
                        <CallbackInfo>
                            {sending ? (
                                <LoaderContainer>
                                    <MiniLoader color={Theme.color.indicator} size={LoaderEnum.small} />
                                </LoaderContainer>
                            ) : servicePhone ? (
                                <ServicePhoneBox>
                                    <Body1 color={Theme.color.secondary}>Позвоните на номер</Body1>
                                    <ServicePhone>{servicePhone}</ServicePhone>
                                    <Body1 color={Theme.color.secondary}>
                                        Мы автоматически подтвердим вход, как только заметим звонок
                                    </Body1>
                                </ServicePhoneBox>
                            ) : (
                                <Body1 color={Theme.color.secondary}>Номер для звонка временно недоступен. Попробуйте позже.</Body1>
                            )}
                        </CallbackInfo>
                    )}
                    {activeWay === 'callback' && error && <ErrorText>{error}</ErrorText>}
                    {activeWay === 'sms' && (
                        <div style={{ marginTop: 16 }}>
                            {sending || verifying ? (
                                <LoaderContainer>
                                    <MiniLoader color={Theme.color.indicator} size={LoaderEnum.small} />
                                </LoaderContainer>
                            ) : countdown ? (
                                <Body1 color={Theme.color.secondary}>Отправить код повторно ({moment.utc(countdown * 1000).format('mm:ss')})</Body1>
                            ) : (
                                <Body1PointerLink onClick={() => handleResend(activeWay)}>Отправить код повторно</Body1PointerLink>
                            )}
                        </div>
                    )}
                    {/* Индикатор общего времени процесса 2FA */}
                    {totalTTLMinutes && (
                        <div style={{ marginTop: 8 }}>
                            <Body1 color={Theme.color.secondary}>
                                Время на подтверждение: {moment.utc(((remainingTTL ?? (totalTTLSeconds)) * 1000)).format('mm:ss')}
                            </Body1>
                        </div>
                    )}
            </Container>
        </StyledPopup>
    )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const WaySelector = styled.div`
  display: flex;
  gap: 12px;
`;

const ErrorText = styled(Body1)`
  color: ${({ theme }) => theme.color.danger};
`;

const ServicePhoneBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: ${({ theme }) => theme.color.secondaryBackground};
  border-radius: 12px;
`;

const ServicePhone = styled(Body1)`
  color: ${({ theme }) => theme.color.indicator};
  font-weight: 600;
`;

const CallbackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Переопределение размеров модалки только для этого места использования
const StyledPopup = styled(Popup)`
  height: 50% !important;
  max-width: 460px !important;
  position: relative; /* нужно для абсолютного позиционирования крестика */
  @media (max-width: 769px) {
    height: 100% !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  /* Второй дочерний div внутри контейнера — это тело модалки (PopupBody) */
  & > div:nth-child(2) {
    height: auto !important; /* Отменяем фиксированную высоту 100% */
  }

  /* Первый дочерний div — это шапка (PopupHeader) */
  & > div:nth-child(1) {
    justify-content: center !important; /* Делаем заголовок центрированным независимо от крестика */
  }

  /* Последний элемент в шапке — кнопка закрытия. Выводим её в правый верхний угол окна */
  & > div:nth-child(1) > div:last-child {
    position: absolute;
    top: 12px;
    right: 12px;
  }
`;


